import type { TelehealthReportGrade } from '@/api/print/types';

import { DrSeverityScale } from './dr-severity-scale';

type ReportSummaryPageProps = {
  grade: TelehealthReportGrade;
};

const DISEASE_FINDINGS: { key: keyof TelehealthReportGrade['disease_presence']; label: string }[] =
  [
    { key: 'cotton_wool_spots', label: 'Cotton Wool Spots' },
    { key: 'hard_exudates', label: 'Hard Exudates' },
    { key: 'microaneurysms', label: 'Microaneurysms' },
    { key: 'macular_edema', label: 'Macular Edema' },
    { key: 'nvd', label: 'NVD' },
    { key: 'nve', label: 'NVE' },
  ];

function RadioCircle({ filled }: { filled: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" className="inline-block">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="#333" strokeWidth="1.5" />
      {filled && <circle cx="7" cy="7" r="3" fill="#333" />}
    </svg>
  );
}

export function ReportSummaryPage({ grade }: ReportSummaryPageProps) {
  return (
    <div className="flex-1 pt-2">
      {/* Title row */}
      <div className="items-baseline mb-1">
        <h2 className="text-lg font-bold text-[#152644]">Findings (DR)</h2>
        <h3 className="text-sm font-bold text-[#152644]">Diabetic Retinopathy Severity Scale</h3>
      </div>

      {/* DR Severity Scale */}
      <div className="mb-3 print-no-break">
        <DrSeverityScale activeScale={grade.dr_grade_scale} />
      </div>

      {/* Disease Presence Table */}
      <div className="mb-3 print-no-break">
        <h3 className="text-sm font-bold text-gray-900 mb-2">Disease Presence</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-400">
              <th className="text-left py-1 font-bold text-gray-900 w-1/3">Finding</th>
              <th className="text-center py-1 font-bold text-gray-900 w-1/3">Present</th>
              <th className="text-center py-1 font-bold text-gray-900 w-1/3">Absent</th>
            </tr>
          </thead>
          <tbody>
            {DISEASE_FINDINGS.map((finding) => {
              const isPresent = grade.disease_presence[finding.key];
              return (
                <tr key={finding.key} className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-800">{finding.label}</td>
                  <td className="py-1.5 text-center">
                    <RadioCircle filled={isPresent} />
                  </td>
                  <td className="py-1.5 text-center">
                    <RadioCircle filled={!isPresent} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Notes and Follow-up Section */}
      <div className="text-xs print-no-break space-y-1 mt-8">
        {grade.notes_label && (
          <p>
            <span className="font-bold">Notes: </span>
            {grade.notes_label.replace(/notes:/gi, ' ')}
          </p>
        )}
        {grade.follow_up.dr_follow_up_label && (
          <p>
            <span className="font-bold">Follow up recommendation: </span>
            {grade.follow_up.dr_follow_up_label}
          </p>
        )}
        {grade.macular_edema_label && <p className="font-bold">{grade.macular_edema_label}</p>}
        {grade.other_disease_label && (
          <p>
            <span className="font-bold">Other Disease: </span>
            {grade.other_disease_label}
          </p>
        )}
        {grade.follow_up.other_disease_follow_up_label && (
          <p>
            <span className="font-bold">Follow up recommendation: </span>
            {grade.follow_up.other_disease_follow_up_label}
          </p>
        )}
      </div>
    </div>
  );
}
